import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../../supabaseClient';
import { STALE } from '../../lib/query/staleTimes';
import { GROUP_MEMBERS_SELECT, mapMembersRows } from '../../lib/groupInfoQueries';
import { useFindGroups } from './useFindGroups';
import { useGroupMemberVoters } from './useEventLineup';

const NO_BLOCKS = new Set();

/** The viewer's groups for one event, upcoming only - the groups whose picks a festival's Sets page can show. */
export function useMyEventGroups(eventId, userId, { authReady = true } = {}) {
  const { data: groups = [], ...rest } = useFindGroups(userId, { authReady });
  const eventGroups = useMemo(
    () => groups.filter((g) => g.eventId === eventId && !g.isPast),
    [groups, eventId],
  );
  return { data: eventGroups, ...rest };
}

/**
 * Who in one group wants to see each artist at this event: `{ members, votersByArtist, totalMemberCount }`.
 * votersReady: the counts have loaded (not just defaulted to empty).
 * The same numbers as the group's Lineup page (useGroupMemberVoters), fetched without that page's hook, which
 * loads the whole group-management surface.
 */
export function useEventGroupVoters({ eventId, groupId, userId }) {
  const { data: members = [] } = useQuery({
    queryKey: ['groups', 'members', groupId],
    enabled: Boolean(groupId && userId),
    staleTime: STALE.user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('group_members')
        .select(GROUP_MEMBERS_SELECT)
        .eq('group_id', groupId)
        .is('left_at', null)
        .order('joined_at', { ascending: true });
      if (error) throw error;
      return mapMembersRows(data);
    },
  });
  const { data: blockedIds = [] } = useQuery({
    queryKey: ['user-blocks', userId],
    enabled: Boolean(userId),
    staleTime: STALE.user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('user_blocks')
        .select('blocked_user_id')
        .eq('blocker_id', userId);
      if (error) throw error;
      return (data ?? []).map((b) => b.blocked_user_id);
    },
  });
  const blockedUsers = useMemo(
    () => (blockedIds.length ? new Set(blockedIds) : NO_BLOCKS),
    [blockedIds],
  );
  const activeMembers = useMemo(
    () => members.filter((m) => !m.left_at && !blockedUsers.has(m.user_id)),
    [members, blockedUsers],
  );
  const { data: votersByArtist = {}, isSuccess: votersReady } = useGroupMemberVoters(
    groupId,
    eventId,
    members,
    blockedUsers,
    {
      enabled: Boolean(groupId && eventId && members.length),
    },
  );
  return {
    members: activeMembers,
    votersByArtist,
    votersReady,
    totalMemberCount: activeMembers.length,
  };
}
